import { useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineSketch } from "@/components/quote/LineSketch";
import { ShapeThumb } from "@/components/settings/ShapeThumb";
import { api, errorMessage } from "@/lib/api";
import { inputCls } from "@/lib/catalog";
import { SHAPE_STYLES } from "@/lib/components";

export const EMPTY_SHAPE = { name: "", style: "half_round", price: 0, unit: "sqft", applies_all: false, line_ids: [], type_ids: [] };

function Checklist({ title, items, chosen, onChange, testId }) {
  const toggle = (id) => onChange(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id]);
  return (
    <div data-testid={testId}>
      <div className="mb-1 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-400">
        {title}
        <span className="flex gap-2 normal-case">
          <button type="button" onClick={() => onChange(items.map((x) => x.id))} className="text-amber-300 hover:underline">All</button>
          <button type="button" onClick={() => onChange([])} className="text-slate-400 hover:underline">None</button>
        </span>
      </div>
      <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-md border border-slate-800 p-2">
        {items.length === 0 && <p className="text-xs text-slate-500">None yet</p>}
        {items.map((x) => (
          <label key={x.id} className="flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" data-testid={`${testId}-${x.id}`} checked={chosen.includes(x.id)} onChange={() => toggle(x.id)} /> {x.name}
          </label>
        ))}
      </div>
    </div>
  );
}

export function AppliesTo({ f, set, lines, types }) {
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" data-testid="shape-editor-applies-all" checked={f.applies_all} onChange={(e) => set("applies_all", e.target.checked)} />
        Applies to all product lines
      </label>
      {!f.applies_all && (
        <div className="grid gap-3 md:grid-cols-2">
          <Checklist title="Product lines" items={lines} chosen={f.line_ids} onChange={(v) => set("line_ids", v)} testId="shape-editor-lines" />
          <Checklist title="Or every line of these types" items={types} chosen={f.type_ids} onChange={(v) => set("type_ids", v)} testId="shape-editor-types" />
        </div>
      )}
    </div>
  );
}

export function ShapeEditor({ shape, lines, types, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY_SHAPE, ...shape, price: Math.round(Number(shape.price || 0) * 10000) / 10000 });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { name: f.name, style: f.style, price: Number(f.price) || 0, unit: f.unit, applies_all: f.applies_all, line_ids: f.line_ids, type_ids: f.type_ids };
      const { data } = shape.id ? await api.put(`/settings/shapes/${shape.id}`, body) : await api.post("/settings/shapes", body);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/settings/shapes/${data.id}/image`, fd);
      }
      toast.success("Shape saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="shape-editor-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto bg-slate-950">
        <DialogHeader><DialogTitle className="font-display text-slate-50">{shape.id ? "Edit shape" : "New shape"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-[1fr_170px]">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-slate-400 sm:col-span-2">Name
              <input data-testid="shape-editor-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></label>
            <label className="text-xs text-slate-400">Drawing style
              <select data-testid="shape-editor-style" value={f.style} onChange={(e) => set("style", e.target.value)} className={inputCls}>
                {SHAPE_STYLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-slate-400">Price $
                <input data-testid="shape-editor-price" inputMode="decimal" value={f.price} onChange={(e) => set("price", e.target.value)} className={`${inputCls} text-right font-mono-num`} /></label>
              <label className="text-xs text-slate-400">Unit
                <select data-testid="shape-editor-unit" value={f.unit} onChange={(e) => set("unit", e.target.value)} className={inputCls}>
                  <option value="sqft">per sq ft</option>
                  <option value="piece">per piece</option>
                </select></label>
            </div>
            <label className="flex items-center gap-3 text-xs text-slate-400 sm:col-span-2">
              <ShapeThumb shape={shape} src={file ? URL.createObjectURL(file) : null} className="h-16 w-16" testId="shape-editor-picture" />
              <span className="inline-flex cursor-pointer items-center gap-1 font-semibold text-amber-300 hover:underline"><ImagePlus className="h-4 w-4" /> {shape.has_image || file ? "Replace picture" : "Add picture"}</span>
              <input type="file" accept=".png,.jpg,.jpeg,.webp" data-testid="shape-editor-picture-input" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
            </label>
          </div>
          <LineSketch params={{ sketch: "fixed", mapped: true, w: 36, h: 48, arch: true, arch_style: f.style }} boxW={170} boxH={190} className="h-48 w-full" testId="shape-editor-preview" />
        </div>
        <AppliesTo f={f} set={set} lines={lines} types={types} />
        <DialogFooter>
          <button data-testid="shape-editor-cancel" onClick={onClose} className="rounded-full px-4 py-2 text-sm text-slate-300 hover:text-slate-50">Cancel</button>
          <button data-testid="shape-editor-save" onClick={save} disabled={busy || !f.name.trim()}
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-40">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
